import { useEffect, useMemo } from 'react'
import { useAliasedLabel, usePublishAliasColumns } from './columnAliasContext'

/**
 * Per-firm column names for the specialized pages that render the two SHARED
 * grids — `ReportDataGrid` and `ReportGroupedGrid` (CC-50291, spec 093).
 *
 * ## Why this file exists
 *
 * CC-50291's first half wired the alias context into `CsvReportGrid` and
 * `AutoReportGrid`, so a firm can rename a column on Timekeeper Analysis, Fee
 * Receipts, P&O and friends. The other twelve catalog rows — Aged A/R, Accounts
 * Payable, and the whole accounting-statement family — render one of the two
 * shared grids instead, and those never told the alias context which columns
 * they had. `SpecializedReportShell` gates its "Rename columns" control on
 * `aliasCtx.columns.length === 0`, so on those pages the control simply never
 * appeared. Nothing needed inventing; the publish was missing.
 *
 * This is that publish — written ONCE and consumed by both grids, rather than
 * the same twenty lines pasted into a 31KB file and a 69KB file.
 *
 * ## `aliasKey` — a column OPTS IN, and it opts in with a stable identity
 *
 * The obvious implementation keys aliases off `columnId`, which every column
 * already has. That would be wrong, and wrong in the specific way this ticket's
 * acceptance criteria call out: **several specialized pages build POSITIONAL
 * columnIds.** Aged A/R uses the array index (`ARReport.jsx`, `columnId: i`) and
 * Balance Sheet uses `c${i}`, because for them `columnId` is an internal pairing
 * between a column and its `dataCells`, not a stored identity. Their visible
 * column set genuinely moves: Aged A/R drops every `isDetail` column when
 * "Show details" is off, and changes shape entirely between its five view modes
 * (measured live, 2026-08-19 — Matter mode publishes 12 columns of which 4 are
 * hidden and 1 is detail-only). An alias stored against index 8 would re-point
 * onto index 8 of a different column set — a mislabelled figure on a financial
 * report, not a cosmetic slip.
 *
 * So the rule is: **a column participates in aliasing if, and only if, it
 * carries `aliasKey`** — its own stable identity, chosen by the page that knows
 * what stable means for it. Three consequences, all deliberate:
 *
 * 1. A positional key cannot become an alias key by accident. A future page that
 *    forgets `aliasKey` gets NO aliases — an honest gap — rather than aliases
 *    pointing at the wrong columns. "When in doubt, do not compute — surface."
 * 2. The generic 119-report path is untouched by construction. Its columns are
 *    aliased upstream in `ReportView` via `applyColumnAliases` and carry no
 *    `aliasKey`, so this hook is inert there.
 * 3. `CsvReportGrid` and `AutoReportGrid` render these same two grids
 *    internally. Their generated columns carry no `aliasKey`, so the inner grid
 *    publishes nothing and cannot clobber the list its wrapper already
 *    published. (`publishAliasColumns` is last-writer-wins; see the empty-list
 *    guard below, which is what actually makes that safe.)
 *
 * ## What this must NEVER do
 *
 * Touch `columnId`. It is the wire key, the pairing key for `dataCells`, and the
 * key every saved view and persisted column preference is stored under. Renaming
 * is cosmetic; the wire contract is not.
 */

/**
 * Unique, stable alias keys from per-column bases.
 *
 * A repeated base gets an **occurrence ordinal**, never a positional index.
 * That distinction is the whole point. Aged A/R's five aging buckets all share
 * `dataField: "numberField"` (verified live across all five view modes), so a
 * bare `dataField` would collapse them into one alias. Numbering them by
 * occurrence gives `numberField`, `numberField~2` … `numberField~5`, which hold
 * still when an unrelated column is hidden — the buckets are structural
 * (Current / 30-59 / 60-89 / 90+ / Total) and CB always emits them in that
 * order. Numbering them by position would not: dropping the detail-only "Name"
 * column shifts every index after it.
 *
 * A blank base falls back to `col`, so blank-headed columns are at least
 * distinguishable from one another; they are the one case where the key is
 * effectively ordinal, and a blank-headed column has no label worth renaming.
 *
 * NOTE: `CsvReportGrid` deliberately keeps its OWN `col${i}` scheme for blank
 * and duplicate CSV headers. It already has aliases stored against those keys in
 * production D1 (measured: `cbTimekeeperAnalysisView` carries `Period` and
 * `Worked Non Billable`), and changing its keys would orphan them. Do not
 * "unify" the two.
 *
 * @param {Array<string|null|undefined>} bases one base per column, in order
 * @returns {string[]} keys, same length, unique
 */
export const stableAliasKeys = (bases) => {
  const seen = new Map()
  return (bases || []).map((b) => {
    const raw = b == null ? '' : String(b)
    const base = raw.trim() === '' ? 'col' : raw
    const n = (seen.get(base) || 0) + 1
    seen.set(base, n)
    return n === 1 ? base : `${base}~${n}`
  })
}

/**
 * Declare a column list's own `columnId`s as its rename keys.
 *
 * For the pages whose `columnId` is already a hand-written stable name —
 * Accounts Payable's `vendor`/`current`/`thirty`, Trial Balance's
 * `account`/`debit`/`credit`, the P&L's `account`/`amount`/`prior`, Calendar's
 * `date`/`title` — identity and pairing key are the same string, so opting in is
 * just saying so. Kept explicit rather than defaulted inside the grid, because
 * the whole point of `aliasKey` is that a page must NAME its stable key: a page
 * with a positional `columnId` must not be able to opt in by omission.
 *
 * Idempotent, and leaves an explicitly-set `aliasKey` alone.
 */
export const withAliasKeys = (columns) =>
  (columns || []).map((c) => (c?.aliasKey != null ? c : { ...c, aliasKey: String(c?.columnId) }))

/**
 * Filter a CB report's columns to the visible ones, apply the firm's aliases to
 * those that opted in, and publish the opted-in set back up to the shell.
 *
 * Drop-in replacement for the identical `(report?.columns || []).filter(...)`
 * memo both grids used to carry.
 *
 * @param {object} report generic `{ columns, reportData, reportFooterCells }`
 * @returns {Array<object>} visible columns, aliased where an alias applies
 */
export const useAliasedGridColumns = (report) => {
  const aliasedLabel = useAliasedLabel()
  const publishAliasColumns = usePublishAliasColumns()

  const visible = useMemo(
    () => (report?.columns || []).filter((c) => c.visible !== false && !c.isBlankColumn),
    [report]
  )

  // Referential identity is preserved when no alias applies — the same
  // discipline `applyColumnAliases.aliasColumns` keeps, and for the same reason:
  // this array feeds a dozen `useMemo` dependency arrays and a `columns` memo
  // inside both grids. A fresh array on every render is how the ticking-value
  // bugs happened. It matters more here than there, because publishing changes
  // the context value, which changes `aliasedLabel`, which re-runs this memo —
  // so without the guard, one publish would churn every downstream memo.
  const columns = useMemo(() => {
    let changed = false
    const next = visible.map((col) => {
      if (col.aliasKey == null) return col
      const label = aliasedLabel(col.aliasKey, col.label)
      if (label === col.label) return col
      changed = true
      // rawLabel is set ONCE, so the editor and the drift check can still see
      // what Centerbase calls this column however many times it passes through.
      return { ...col, label, rawLabel: col.rawLabel ?? col.label }
    })
    return changed ? next : visible
  }, [visible, aliasedLabel])

  // Publish the columns an admin could rename. CB's OWN label, never the
  // aliased one — the editor shows both, side by side.
  useEffect(() => {
    if (!publishAliasColumns) return
    const list = columns
      .filter((c) => c.aliasKey != null)
      .map((c) => ({ key: String(c.aliasKey), label: String(c.rawLabel ?? c.label ?? '') }))
    // Never publish an EMPTY list. On a `CsvReportGrid` / `AutoReportGrid` page
    // this hook also runs in the inner grid, whose columns carry no `aliasKey`.
    // Publishing `[]` from there would wipe the real list its wrapper published
    // the moment the inner grid re-rendered on its own (a sort, a filter) —
    // silently un-shipping the twelve reports that already work. Staying silent
    // makes the inner grid genuinely inert instead of merely usually-harmless.
    if (!list.length) return
    publishAliasColumns(list)
  }, [publishAliasColumns, columns])

  return columns
}

export default useAliasedGridColumns
